import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useState } from "react";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { useHistory } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { auth, db } from "../Config/firebase";
import { gerarSequencial, obterMaiorSequencial } from "../Utils/sequencial";
import RelatorioRupturaPaverPrint from "./RelatorioRupturaPaverPrint";
import {
  DIAMETRO_PADRAO_PAVER_MM,
  calcularAreaPorDiametro,
  calcularCorposProva,
  criarCorposProva,
  formatarDataBR,
} from "./paverCalculos";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";

const COLLECTION = "tblRupturaPavers";

const estadoInicial = {
  codigo: "",
  dataRelatorio: "",
  cliente: "",
  clienteId: "",
  obra: "",
  obraId: "",
  endereco: "",
  local: "",
  localAplicacao: "",
  caracteristicaPiso: "",
  procedencia: "",
  lote: "",
  dimensoesDeclaradas: "",
  resistenciaEsperada: "",
  resistenciaEsperadaNaoInformada: false,
  formatoTipo: "",
  dataFabricacao: "",
  dataFabricacaoNaoInformada: false,
  dataEnsaio: "",
  idadeDeclarada: "",
  quantidadeAmostras: "",
  diametroCorpoProva: "",
  areaCorpoProva: "",
  coeficienteStudent: "",
  comentarios: "",
  observacao: "",
  prensaRuptura: null,
  prensaDescricao: "",
  dataCalibracaoPrensa: "",
  certificadoPrensa: "",
  corposProva: [],
};

const opcoesFormatoTipo = [
  {
    tipo: "I",
    texto: "TIPO I Conforme NBR 9781 Anexo B",
    descricao: "Retangular ou similar, assentado em fileiras ou espinha de peixe.",
  },
  {
    tipo: "II",
    texto: "TIPO II Conforme NBR 9781 Anexo B",
    descricao: "Formato unico, assentado somente em fileiras.",
  },
  {
    tipo: "III",
    texto: "TIPO III Conforme NBR 9781 Anexo B",
    descricao: "Formatos geometricos caracteristicos, com peso superior a 4 kg.",
  },
  {
    tipo: "IV",
    texto: "TIPO IV Conforme NBR 9781 Anexo B",
    descricao: "Conjunto de componentes com diferentes tamanhos ou juntas falsas.",
  },
];

function calcularProximoCodigo(lista) {
  const ano = new Date().getFullYear();
  const numeros = lista
    .map((item) => String(item.codigo || "").match(/CE-RE-PE-(\d+)\/\d{4}/))
    .filter(Boolean)
    .map((match) => Number(match[1]))
    .filter(Number.isFinite);

  const proximo = numeros.length ? Math.max(...numeros) + 1 : lista.length + 1;
  return `CE-RE-PE-${String(proximo).padStart(3, "0")}/${ano}`;
}

function normalizarTexto(valor) {
  return String(valor || "").toLowerCase();
}

function extrairValorResistencia(valor) {
  return String(valor || "").replace(/[^\d,.]/g, "");
}

function formatarResistenciaEsperada(valor) {
  const numero = extrairValorResistencia(valor).trim();
  return numero ? `fpk ≥ ${numero} MPa` : "";
}

function prepararDadosEnsaioPaver(formulario) {
  return {
    ...formulario,
    resistenciaEsperada: formulario.resistenciaEsperadaNaoInformada
      ? ""
      : formatarResistenciaEsperada(formulario.resistenciaEsperada),
  };
}

function MiniaturaFormatoTipo({ tipo }) {
  const imagens = {
    I: "/Images/paver-tipo-i-nbr9781.png",
    II: "/Images/paver-tipo-ii-nbr9781.png",
    III: "/Images/paver-tipo-iii-nbr9781.png",
    IV: "/Images/paver-tipo-iv-nbr9781.png",
  };

  return (
    <Box
      component="img"
      src={`${process.env.PUBLIC_URL}${imagens[tipo]}`}
      alt={`Exemplos de componentes de concreto do tipo ${tipo}, conforme NBR 9781 Anexo B`}
      sx={{
        display: "block",
        height: tipo === "IV" ? 210 : 130,
        objectFit: "contain",
        width: "100%",
      }}
    />
  );
}

export default function CadastroRupturaPaver() {
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const history = useHistory();
  const [usuario, setUsuario] = useState(null);
  const [ensaios, setEnsaios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [obrasSistema, setObrasSistema] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [form, setForm] = useState(estadoInicial);
  const [openDialog, setOpenDialog] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [ensaioEditando, setEnsaioEditando] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("");
  const [modoPrint, setModoPrint] = useState(false);
  const [ensaioPrint, setEnsaioPrint] = useState(null);
  const [autoPrint, setAutoPrint] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUsuario(null);
        return;
      }

      const snap = await getDoc(doc(db, "users", user.uid));
      setUsuario(snap.exists() ? { uid: user.uid, ...snap.data() } : { uid: user.uid });
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    carregarEnsaios();
    carregarClientes();
  }, []);

  async function carregarEnsaios() {
    setLoading(true);
    const q = query(collection(db, COLLECTION), orderBy("criadoEm", "desc"));
    const snap = await getDocs(consultaTenantAtual(q));
    setEnsaios(snap.docs.map((documento) => ({ id: documento.id, ...documento.data() })));
    setLoading(false);
  }

  async function carregarClientes() {
    const q = query(collection(db, "clientes"), orderBy("nome", "asc"));
    const snap = await getDocs(consultaTenantAtual(q));
    setClientes(snap.docs.map((documento) => ({ id: documento.id, ...documento.data() })));
  }

  async function carregarObrasPorCliente(clienteId) {
    if (!clienteId) {
      setObrasSistema([]);
      return;
    }

    const q = query(
      collection(db, "obras"),
      where("clienteId", "==", clienteId),
      orderBy("nome", "asc")
    );
    const snap = await getDocs(consultaTenantAtual(q));
    setObrasSistema(snap.docs.map((documento) => ({ id: documento.id, ...documento.data() })));
  }

  function abrirNovoCadastro() {
    setModoEdicao(false);
    setEnsaioEditando(null);
    setClienteSelecionado(null);
    setObrasSistema([]);
    setForm({
      ...estadoInicial,
      corposProva: criarCorposProva(10),
    });
    setOpenDialog(true);
  }

  async function editarEnsaio(ensaio) {
    setModoEdicao(true);
    setEnsaioEditando(ensaio);

    const clienteEncontrado = clientes.find((cliente) => cliente.id === ensaio.clienteId);
    setClienteSelecionado(clienteEncontrado || null);

    if (ensaio.clienteId) {
      await carregarObrasPorCliente(ensaio.clienteId);
    }

    setForm({
      ...estadoInicial,
      ...ensaio,
      quantidadeAmostras: ensaio.quantidadeAmostras || ensaio.corposProva?.length || 10,
      corposProva: ensaio.corposProva?.length ? ensaio.corposProva : criarCorposProva(10),
    });
    setOpenDialog(true);
  }

  function selecionarObra(obraId) {
    const obraSelecionada = obrasSistema.find((obra) => obra.id === obraId);

    setForm({
      ...form,
      obra: obraSelecionada?.nome || "",
      obraId: obraSelecionada?.id || "",
      endereco: obraSelecionada?.endereco || "",
      local: obraSelecionada?.endereco || obraSelecionada?.local || "",
      localAplicacao: obraSelecionada?.nome || "",
    });
  }

  async function salvarEnsaio() {
    if (!form.clienteId) {
      alert("Selecione o cliente antes de salvar.");
      return;
    }

    const quantidade = Number(form.quantidadeAmostras || 10);
    const areaCalculada = calcularAreaPorDiametro(
      form.diametroCorpoProva || DIAMETRO_PADRAO_PAVER_MM
    );
    const corposAtuais = Array.isArray(form.corposProva) ? form.corposProva : [];
    const corposProva =
      corposAtuais.length === quantidade ? corposAtuais : criarCorposProva(quantidade);
    const codigo = modoEdicao
      ? form.codigo
      : form.codigo || (await gerarSequencial({
          chave: "ruptura_paver",
          escopo: new Date(form.dataRelatorio || new Date()).getFullYear(),
          formato: {
            padding: 3,
            ano: new Date(form.dataRelatorio || new Date()).getFullYear(),
            template: "CE-RE-PE-{numero}/{ano}",
          },
          valorMinimo: obterMaiorSequencial(ensaios, /CE-RE-PE-(\d+)\/\d{4}/),
        })).codigo;

    const dados = {
      ...prepararDadosEnsaioPaver(form),
      codigo,
      areaCorpoProva: areaCalculada.toFixed(1),
      quantidadeAmostras: quantidade,
      procedencia: form.procedencia || form.cliente,
      corposProva: calcularCorposProva(corposProva, areaCalculada),
      atualizadoEm: new Date(),
      atualizadoPor: usuario?.uid || null,
    };

    if (modoEdicao && ensaioEditando?.id) {
      await atualizarDocumentoComMesclagem({
        db,
        referencia: doc(db, COLLECTION, ensaioEditando.id),
        original: ensaioEditando,
        atual: { ...ensaioEditando, ...dados },
        metadados: { atualizadoEm: dados.atualizadoEm, atualizadoPor: dados.atualizadoPor },
      });
    } else {
      await addDoc(collection(db, COLLECTION), aplicarTenantAtual({
        ...dados,
        criadoEm: new Date(),
        criadoPor: usuario?.uid || null,
      }));
    }

    setOpenDialog(false);
    await carregarEnsaios();
  }

  function abrirRelatorio(ensaio, imprimir = false) {
    setEnsaioPrint(ensaio);
    setAutoPrint(imprimir);
    setModoPrint(true);
  }

  const ensaiosFiltrados = useMemo(() => {
    const termo = normalizarTexto(filtro);
    if (!termo) return ensaios;

    return ensaios.filter((item) =>
      [item.codigo, item.cliente, item.obra, item.localAplicacao, item.dataEnsaio].some((valor) =>
        contemTextoPesquisa(normalizarTexto(valor), termo)
      )
    );
  }, [ensaios, filtro]);

  if (modoPrint && ensaioPrint) {
    return (
      <RelatorioRupturaPaverPrint
        ensaio={ensaioPrint}
        fotosRelatorio={(ensaioPrint.relatorioFotografico || []).filter((foto) => foto.url)}
        autoPrint={autoPrint}
        onVoltar={() => {
          setModoPrint(false);
          setAutoPrint(false);
          setEnsaioPrint(null);
        }}
      />
    );
  }

  return (
    <>
      <Navbar />

      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0}>
        <Typography variant="h6" fontWeight={700}>Ruptura de Paver</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>Lista de ensaios cadastrados</Typography>
        <Stack direction={{ xs: "column", md: "row" }} alignItems="stretch" spacing={2} mb={3}>
          <Button variant="contained" onClick={abrirNovoCadastro}>
            + Novo Ensaio
          </Button>

          <TextField
            size="small"
            label="Pesquisar"
            placeholder="Código, cliente, obra, local ou data"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            sx={{ flex: 1 }}
          />

          <TextField
            size="small"
            label="Qtd Ensaios"
            value={ensaiosFiltrados.length}
            InputProps={{ readOnly: true }}
            sx={{ width: { xs: "100%", md: 140 } }}
          />
        </Stack>
        </Paper>

        <Paper sx={{ borderRadius: "12px", overflow: "auto", border: "1px solid #e2e8f0", boxShadow: "none" }}>
          <Table className="lista-cadastro-padrao" size="small" sx={{ minWidth: 900 }}>
            <TableHead sx={{ backgroundColor: "#f8fafc" }}>
              <TableRow>
                <TableCell><strong>Código</strong></TableCell>
                <TableCell>Cliente / Obra</TableCell>
                <TableCell>Data ensaio</TableCell>
                <TableCell>Local aplicação</TableCell>
                <TableCell align="center">Amostras</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ensaiosFiltrados.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{ height: 48, cursor: "pointer" }}
                  onClick={() => history.push(`/app/ruptura-paver/${item.id}`)}
                >
                  <TableCell>{item.codigo || "-"}</TableCell>
                  <TableCell>
                    <div className="lista-dado-principal">{item.cliente || "-"}</div>
                    <div className="lista-dado-secundario">Obra: {item.obra || "-"}</div>
                  </TableCell>
                  <TableCell className="lista-dado-secundario">{formatarDataBR(item.dataEnsaio)}</TableCell>
                  <TableCell className="lista-dado-destaque">{item.localAplicacao || item.local || "-"}</TableCell>
                  <TableCell align="center">{item.quantidadeAmostras || item.corposProva?.length || 0}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Visualizar">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          abrirRelatorio(item, false);
                        }}
                      >
                        <VisibilityIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </Tooltip>
                    <HistoricoCadastro colecao={COLLECTION} documentoId={item.id} registro={item} />
                    <Tooltip title="Editar cadastro">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          editarEnsaio(item);
                        }}
                      >
                        <EditIcon sx={{ color: "#0288d1" }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {loading && <TableRow><TableCell colSpan={6}><Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, py: 4 }}><CircularProgress /><Typography color="text.secondary">Carregando registros...</Typography></Box></TableCell></TableRow>}
              {!loading && ensaiosFiltrados.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Nenhum ensaio cadastrado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog open={openDialog} onClose={() => !salvandoCadastro && setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>{modoEdicao ? "Editar Ruptura de Paver" : "Nova Ruptura de Paver"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField label="Código" value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} fullWidth />

            <Autocomplete
              options={clientes}
              getOptionLabel={(option) => option?.nome || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={clienteSelecionado}
              onChange={(event, newValue) => {
                setClienteSelecionado(newValue);
                setForm({
                  ...form,
                  cliente: newValue?.nome || "",
                  clienteId: newValue?.id || "",
                  procedencia: newValue?.nome || "",
                  obra: "",
                  obraId: "",
                  local: "",
                  endereco: "",
                });
                carregarObrasPorCliente(newValue?.id);
              }}
              renderInput={(params) => <TextField {...params} label="Cliente" placeholder="Pesquisar cliente..." fullWidth />}
            />

            <TextField select label="Empreendimento/Obra" value={form.obraId || ""} onChange={(e) => selecionarObra(e.target.value)} fullWidth disabled={!clienteSelecionado}>
              {obrasSistema.map((obra) => (
                <MenuItem key={obra.id} value={obra.id}>
                  {obra.nome}
                </MenuItem>
              ))}
            </TextField>

            <Box display="grid" gridTemplateColumns="1fr" gap={2}>
              <TextField label="Data do relatório" type="date" InputLabelProps={{ shrink: true }} value={form.dataRelatorio} onChange={(e) => setForm({ ...form, dataRelatorio: e.target.value })} />
              <TextField label="Data do ensaio" type="date" InputLabelProps={{ shrink: true }} value={form.dataEnsaio} onChange={(e) => setForm({ ...form, dataEnsaio: e.target.value })} />
              <Stack spacing={0.5}>
                <TextField
                  label="Data de fabricação/lote"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={form.dataFabricacao}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      dataFabricacao: e.target.value,
                      lote: e.target.value,
                    })
                  }
                  disabled={form.dataFabricacaoNaoInformada === true}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.dataFabricacaoNaoInformada === true}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          dataFabricacaoNaoInformada: e.target.checked,
                          dataFabricacao: e.target.checked
                            ? ""
                            : form.dataFabricacao,
                          lote: e.target.checked ? "" : form.lote,
                        })
                      }
                    />
                  }
                  label="Não informada"
                />
              </Stack>
              <TextField label="Quantidade de amostras" type="number" value={form.quantidadeAmostras} onChange={(e) => setForm({ ...form, quantidadeAmostras: e.target.value })} />
              <TextField label="Diâmetro do corpo de prova (mm)" value={form.diametroCorpoProva || ""} onChange={(e) => {
                const areaCalculada = e.target.value ? calcularAreaPorDiametro(e.target.value) : "";
                setForm({ ...form, diametroCorpoProva: e.target.value, areaCorpoProva: areaCalculada ? areaCalculada.toFixed(1) : "" });
              }} />
            </Box>

            <TextField label="Característica do piso" value={form.caracteristicaPiso} onChange={(e) => setForm({ ...form, caracteristicaPiso: e.target.value })} fullWidth />
            <TextField label="Local de aplicação" value={form.localAplicacao} onChange={(e) => setForm({ ...form, localAplicacao: e.target.value })} fullWidth />
            <TextField label="Dimensões declaradas" value={form.dimensoesDeclaradas} onChange={(e) => setForm({ ...form, dimensoesDeclaradas: e.target.value })} fullWidth />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
              <TextField
                label="Resistência esperada (MPa)"
                value={extrairValorResistencia(form.resistenciaEsperada)}
                onChange={(e) => setForm({ ...form, resistenciaEsperada: extrairValorResistencia(e.target.value) })}
                disabled={form.resistenciaEsperadaNaoInformada === true}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.resistenciaEsperadaNaoInformada === true}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        resistenciaEsperadaNaoInformada: e.target.checked,
                        resistenciaEsperada: e.target.checked
                          ? ""
                          : form.resistenciaEsperada,
                      })
                    }
                  />
                }
                label="Não informada"
                sx={{ whiteSpace: "nowrap" }}
              />
            </Stack>
            <TextField
              select
              label="Formato tipo"
              value={form.formatoTipo}
              onChange={(e) => setForm({ ...form, formatoTipo: e.target.value })}
              fullWidth
              SelectProps={{
                renderValue: (value) => value || "Selecione o formato",
                MenuProps: {
                  PaperProps: {
                    sx: { maxHeight: 560 },
                  },
                },
              }}
            >
              <MenuItem value="">
                <em>Selecione o formato</em>
              </MenuItem>
              {opcoesFormatoTipo.map((opcao) => (
                <MenuItem key={opcao.tipo} value={opcao.texto} sx={{ alignItems: "stretch", py: 1.25 }}>
                  <Box
                    sx={{
                      border: form.formatoTipo === opcao.texto ? "2px solid #1976d2" : "1px solid #cfd8dc",
                      borderRadius: 1,
                      display: "grid",
                      gap: 1,
                      p: 1,
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography fontWeight={700} sx={{ minWidth: 54 }}>
                        Tipo {opcao.tipo}
                      </Typography>
                      <Typography variant="body2">{opcao.texto}</Typography>
                    </Box>
                    <MiniaturaFormatoTipo tipo={opcao.tipo} />
                    <Typography variant="caption" color="text.secondary">
                      {opcao.descricao}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={salvandoCadastro}>Cancelar</Button>
          <Button variant="contained" onClick={() => executarSalvamento(salvarEnsaio)} disabled={salvandoCadastro}>
            {salvandoCadastro ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
